import { create } from 'zustand';
import type { PersonaId, CreditTier, UserClass } from '@/types/persona';

export interface User {
  id: string;
  email: string;
  name: string;
  orgId: string;
  orgName: string;
  persona: PersonaId;
  roles: string[];
  divisions: string[];
  tier: CreditTier;
  creditsRemaining: number;
  // Multi-vertical sign-up fields (may be undefined for legacy users)
  userClass?: UserClass;
  agencyLevel?: string;
  primaryVerticals?: string[];
  stateTerritory?: string;
  verticalProfile?: Record<string, unknown>;
}

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuth: (token: string, user: User) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
  updateCredits: (credits: number) => void;
  updateVerticalProfile: (profile: Record<string, unknown>) => void;
}

const TOKEN_KEY = 'zoneium_token';
const USER_KEY = 'zoneium_user';
const USER_CACHED_AT_KEY = 'zoneium_user_cached_at';

/**
 * Maximum age of the cached user object before we treat it as stale
 * and force a /auth/refresh round-trip. 1 hour is short enough that
 * server-side role/tier changes propagate "soon" but long enough that
 * the cache absorbs typical short tab-reopens without paying the
 * round-trip every time.
 */
const USER_CACHE_TTL_MS = 60 * 60 * 1000;

/**
 * Persist the user object alongside the token so the avatar + greeting
 * don't flash to "U / User" between hard-refresh and the AuthGuard's
 * background `refresh()` round-trip. The store rehydrates synchronously
 * from localStorage on module evaluation; the AuthGuard then refreshes
 * the user object via `/auth/refresh` (which re-reads from BigQuery)
 * to pick up server-side changes — roles, tier, credits, persona.
 *
 * The cached copy is intentionally short-lived (see USER_CACHE_TTL_MS):
 * if the cache is older than the TTL we drop it on read so the next
 * AuthGuard mount has to round-trip. This prevents the indefinite-cache
 * bug where role promotions never reached the FE because the cached
 * `user.roles` was frozen at first-login forever.
 */
function readPersistedUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;

    const cachedAtRaw = localStorage.getItem(USER_CACHED_AT_KEY);
    const cachedAt = cachedAtRaw ? Number.parseInt(cachedAtRaw, 10) : 0;
    const isStale =
      !cachedAt || Number.isNaN(cachedAt) || Date.now() - cachedAt > USER_CACHE_TTL_MS;
    if (isStale) {
      // Drop the stale copy so the AuthGuard's refresh effect treats
      // this as a missing-user case and pulls fresh from the server.
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(USER_CACHED_AT_KEY);
      return null;
    }

    const parsed = JSON.parse(raw) as User;
    // Quick shape check — drop anything that doesn't look like a User
    // (e.g. left over from a prior schema).
    if (parsed && typeof parsed === 'object' && typeof parsed.id === 'string') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedUser(user: User): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(USER_CACHED_AT_KEY, String(Date.now()));
  } catch {
    // localStorage quota or disabled — token still wins; user re-fetches
    // on the next AuthGuard refresh tick.
  }
}

const initialToken = localStorage.getItem(TOKEN_KEY);
const initialUser = initialToken ? readPersistedUser() : null;

export const useAuthStore = create<AuthState>((set) => ({
  token: initialToken,
  user: initialUser,
  // Treat the user as authenticated on mount when both token + user are
  // already in localStorage. The AuthGuard still validates the token's
  // expiry on every render, so a stale token will redirect to /auth.
  isAuthenticated: !!(initialToken && initialUser),
  isLoading: !initialUser && !!initialToken, // mid-rehydration when token but no user
  setAuth: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    writePersistedUser(user);
    set({ token, user, isAuthenticated: true, isLoading: false });
  },
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_CACHED_AT_KEY);
    set({ token: null, user: null, isAuthenticated: false, isLoading: false });
  },
  setLoading: (isLoading) => set({ isLoading }),
  updateCredits: (creditsRemaining) =>
    set((state) => {
      if (!state.user) return state;
      const next = { ...state.user, creditsRemaining };
      writePersistedUser(next);
      return { user: next };
    }),
  updateVerticalProfile: (verticalProfile) =>
    set((state) => {
      if (!state.user) return state;
      const next = { ...state.user, verticalProfile };
      writePersistedUser(next);
      return { user: next };
    }),
}));
